import { useQuery } from "@tanstack/react-query";
import { fetchWithAuth } from "./fetchWithAuth";
import { useGlobalStore } from "../context/UserContext/userContext";
import { formatStudyData } from "./helper";
import { AUTH_SOURCE, usesReportingGetUserStudy } from "../constants/authSource";
import { getReportingApiBaseUrl, getStudyApiBaseUrl } from "../config/apiBaseUrls";
import { reportingClient } from "../api/httpClients/reportingClient";

export const useStudyById = (studyId) => {
  return useQuery({
    queryKey: ["studyById", studyId],
    queryFn: () => fetchCurrentStudy(studyId),
    enabled: !!studyId,
  });
};

export const fetchCurrentStudy = async (studyId) => {
  const { authSource } = useGlobalStore.getState();

  if (usesReportingGetUserStudy(authSource)) {
    const url = new URL(`${getReportingApiBaseUrl()}/api/Report/GetUserStudy`);
    const response = await fetchWithAuth(url, {
      method: "POST",
      body: JSON.stringify({}),
    });
    const data = await response.json();
    return formatStudyData(data?.payload);
  }

  const url = new URL(`${getStudyApiBaseUrl()}/api/study/getStudyById`);
  url.searchParams.set("studyId", studyId);
  const response = await fetchWithAuth(url, { method: "GET" });
  const data = await response.json();
  return data.payload;
};

// Fetch user files for CRE and Radical users
export const getUserFiles = async () => {
  const response = await reportingClient.post("/api/Report/GetUserFiles", {});
  const data = response?.data;
  // Normalize the response to match expected format
  const { authSource } = useGlobalStore.getState();
  if (authSource === AUTH_SOURCE.CRE) {
    return normalizeReportingFiles(data.payload);
  }
  // For Radical users, normalize to ensure consistent structure
  return normalizeFiles(data.payload || []);
};

/**
 * Normalize file response to ensure consistent structure:
 * Converts file objects to { fileId, url, fileName, type }
 */
export function normalizeFiles(files) {
  if (!Array.isArray(files)) return [];
  return files.map((f) => {
    // Extract type from file name if not present
    const type =
      f.type ||
      (f.fileName || f.name || "")
        .split(".")
        ?.pop()
        ?.toLowerCase() || "";

    return {
      fileId: f.fileId || f.id,
      url: f.url || null,
      fileName: f.fileName || f.name || "",
      type: type,
      documentType: f.documentType || f.document_type || null,
    };
  });
}

/**
 * Normalize Reporting (CRE) file response to the Radical format:
 * Converts Reporting file objects to { fileId, url, fileName, type }
 */
export function normalizeReportingFiles(files) {
  if (!Array.isArray(files)) return [];
  return files.map((f) => {
    // Extract type from file name if not present
    const type =
      f.name
        .split(".")
        ?.pop()
        ?.toLowerCase() || "";

    return {
      fileId: f.fileId,
      url: f.url || null,
      fileName: f.name || "",
      type,
      documentType: f.documentType || f.document_type || null,
    };
  });
}

export const updateUserStudy = async (updateObj) => {
  const response = await reportingClient.post("/api/Report/UpdateUserStudy", updateObj);
  const data = response?.data;
  if (data?.success && data?.payload) return formatStudyData(data.payload);
  return null;
};

export const deleteUserFiles = async (fileIds) => {
  const response = await reportingClient.post("/api/Report/DeleteFiles", { fileIds });
  return response?.data;
};

// Upload Files - Works for both CRE and Radical users
export const uploadFiles = async (files, documentType) => {
  if (!files || files.length === 0) throw new Error("No files to upload");
  if (!documentType) throw new Error("document_type is required");

  const endpoint = "/api/Report/UploadFiles";
  const formData = new FormData();
  files.forEach((file) => {
    formData.append("Files", file);
  });
  formData.append("document_type", documentType);
  try {
    const response = await reportingClient.post(endpoint, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      maxBodyLength: Infinity,
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading files:", {
      message: error.message,
      config: error.config,
      response: error.response
        ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers,
          }
        : null,
    });
    throw error;
  }
};

export const getScannedDocsSummary = async () => {
  const response = await reportingClient.post("/api/Report/GetScannedDocsSummary", {});
  return response?.data;
};

// Get User Credits (CRE)
export const getUserCredits = async () => {
  const response = await reportingClient.post("/api/Report/GetUserCredits", {});
  return response?.data;
};

// Reset user data for a fresh report session (CRE/Reporting)
export const resetUserData = async () => {
  const response = await reportingClient.post("/api/Report/ResetUserData", {});
  return response?.data;
};
